/**
 * B2BDocumentView.jsx — Page partenaire : visualisation + signature électronique
 *
 * Flow :
 *   1. Charge le document via le token (/api/b2b/documents/:token)
 *   2. Affiche le HTML dans un iframe (via /api/b2b/documents/:token/html)
 *   3. Si pas signé : affiche le composant signature (canvas 300x120, trait 2px #0F172A)
 *   4. Case à cocher obligatoire + nom + email avant validation
 *   5. POST /sign : enregistre image + IP + horodatage + hash
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Loader2, ShieldCheck, Check, Eraser, Hash, Mail, User, Calendar, FileText,
} from 'lucide-react';
import api, { API_URL } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const DOC_LABEL = { devis: 'Devis', contrat: 'Contrat', facture: 'Facture' };

export default function B2BDocumentView() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Signature form
  const canvasRef = useRef(null);
  const [checked, setChecked] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [hasDrawn, setHasDrawn] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const htmlUrl = `${API_URL}/api/b2b-docs/${token}/html`;

  const loadDoc = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const d = await api.b2bDocPublicGet(token);
      setDoc(d);
    } catch (e) {
      setError(e.message || 'Document introuvable');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadDoc(); }, [loadDoc]);

  // Canvas drawing setup
  useEffect(() => {
    if (!canvasRef.current || doc?.status === 'signed') return;
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0F172A';

    let drawing = false;
    const pos = (e) => {
      const r = c.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return {
        x: (t.clientX - r.left) * (c.width / r.width),
        y: (t.clientY - r.top) * (c.height / r.height),
      };
    };
    const down = (e) => { drawing = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault && e.preventDefault(); };
    const move = (e) => { if (!drawing) return; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasDrawn(true); e.preventDefault && e.preventDefault(); };
    const up = () => { drawing = false; };
    c.addEventListener('mousedown', down);
    c.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    c.addEventListener('touchstart', down);
    c.addEventListener('touchmove', move);
    c.addEventListener('touchend', up);
    return () => {
      c.removeEventListener('mousedown', down);
      c.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      c.removeEventListener('touchstart', down);
      c.removeEventListener('touchmove', move);
      c.removeEventListener('touchend', up);
    };
  }, [doc]);

  const clearCanvas = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    setHasDrawn(false);
  };

  const canSubmit = checked && hasDrawn && signerName.trim().length > 1 && /@/.test(signerEmail);

  const submit = async () => {
    if (!canSubmit) {
      toast.error('Vérifiez : case cochée, signature tracée, nom et email');
      return;
    }
    setSubmitting(true);
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      const res = await api.b2bDocPublicSign(token, {
        signature_image: dataUrl,
        checkbox_accepted: true,
        signer_name: signerName.trim(),
        signer_email: signerEmail.trim(),
        signer_date: new Date().toLocaleDateString('fr-FR'),
      });
      if (res?.email_sent) {
        toast.success('Signature enregistrée — Confirmation envoyée par email');
      } else if (res?.email_error) {
        toast.success('Signature enregistrée (email de confirmation non livré, le document reste valide)');
      } else {
        toast.success('Signature enregistrée — Document finalisé');
      }
      await loadDoc();
    } catch (e) {
      toast.error(e.message || 'Signature impossible');
    } finally {
      setSubmitting(false);
    }
  };

  // ===== Loading / Error =====
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="bg-white border border-red-200 rounded-xl p-6 max-w-md text-center shadow-sm">
          <div className="text-red-600 font-semibold mb-1">Document indisponible</div>
          <div className="text-sm text-slate-600">{error}</div>
        </div>
      </div>
    );
  }

  const isSigned = doc?.status === 'signed';
  const sig = doc?.signature || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="b2b-doc-view">
      {/* Top bar */}
      <div className="bg-slate-900 text-white px-4 py-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-orange-400" />
          <div>
            <div className="font-semibold text-sm" style={{fontFamily:'Fraunces, serif'}}>
              {DOC_LABEL[doc.doc_type] || doc.doc_type} N° {doc.doc_number}
            </div>
            <div className="text-xs text-slate-400">
              {doc.client?.raison_sociale} — {doc.doc_date}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isSigned ? (
            <Badge className="bg-green-500 text-white">
              <Check className="w-3 h-3 mr-1" /> Document signé
            </Badge>
          ) : (
            <Badge className="bg-orange-400 text-slate-900">
              En attente de signature
            </Badge>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-3 md:px-6 py-4 md:py-6">
        {/* Document preview */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" data-testid="doc-iframe-wrap">
          <iframe
            src={htmlUrl}
            title={`${DOC_LABEL[doc.doc_type]} ${doc.doc_number}`}
            className="w-full"
            style={{ minHeight: '70vh', border: 'none' }}
          />
        </div>

        {/* Signature section */}
        <div className="mt-5 bg-white rounded-xl shadow-sm border border-slate-200 p-4 md:p-6">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-5 h-5 text-orange-500" />
            <h2 className="text-lg font-serif font-bold" style={{fontFamily:'Fraunces, serif'}}>
              Signature électronique — eIDAS simple
            </h2>
          </div>

          {isSigned ? (
            // ===== DISPLAY SIGNED PROOF =====
            <div className="space-y-3" data-testid="signed-proof">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 font-semibold text-green-800 mb-2">
                  <Check className="w-5 h-5" /> Document signé et archivé
                </div>
                {sig.image_data_url && (
                  <img src={sig.image_data_url} alt="Signature" className="bg-white border rounded max-h-28 mb-3" />
                )}
                <div className="grid md:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-start gap-2">
                    <User className="w-4 h-4 text-slate-500 mt-0.5" />
                    <div><div className="text-xs text-slate-500">Signataire</div><div className="font-medium">{sig.signer_name}</div></div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Mail className="w-4 h-4 text-slate-500 mt-0.5" />
                    <div><div className="text-xs text-slate-500">Email</div><div className="font-medium">{sig.signer_email}</div></div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Calendar className="w-4 h-4 text-slate-500 mt-0.5" />
                    <div><div className="text-xs text-slate-500">Horodatage</div><div className="font-medium">{doc.signed_at || '—'}</div></div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Hash className="w-4 h-4 text-slate-500 mt-0.5" />
                    <div><div className="text-xs text-slate-500">Hash SHA-256</div><div className="font-mono text-xs break-all">{sig.hash_at_signature}</div></div>
                  </div>
                  <div className="flex items-start gap-2 md:col-span-2">
                    <div className="text-xs text-slate-500 shrink-0">IP :</div>
                    <div className="font-mono text-xs">{sig.ip || '—'}</div>
                  </div>
                </div>
              </div>
              <p className="text-xs text-slate-500 italic">
                Règlement eIDAS UE 910/2014 — Signature électronique simple avec même valeur juridique que la signature manuscrite.
              </p>
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => window.print()}>Imprimer</Button>
              </div>
            </div>
          ) : (
            // ===== SIGN FORM =====
            <div className="space-y-4" data-testid="sign-form">
              <p className="text-xs text-slate-600">
                En cochant la case et en apposant votre signature, vous reconnaissez avoir lu, compris et accepté l'intégralité du document. Signature électronique de même valeur qu'une signature manuscrite — Règlement eIDAS UE 910/2014. Horodatage + IP + hash conservés comme preuve.
              </p>

              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>Nom et prénom du signataire *</Label>
                  <Input
                    placeholder="Ex : DURAND Jean"
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    data-testid="input-signer-name"
                  />
                </div>
                <div>
                  <Label>Email du signataire *</Label>
                  <Input
                    type="email"
                    placeholder="contact@auto-ecole.fr"
                    value={signerEmail}
                    onChange={(e) => setSignerEmail(e.target.value)}
                    data-testid="input-signer-email"
                  />
                </div>
              </div>

              <label className="flex items-start gap-2 text-sm cursor-pointer bg-slate-50 border border-slate-200 rounded-lg p-3">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                  data-testid="check-approuve"
                />
                <span>
                  J'ai lu, compris et j'accepte l'intégralité du document — <b>Lu et approuvé — Bon pour accord</b>.
                </span>
              </label>

              <div>
                <Label className="mb-2 block">Signature manuscrite (tracez avec la souris ou le doigt) *</Label>
                <div className="inline-block bg-white">
                  <canvas
                    ref={canvasRef}
                    width={300}
                    height={120}
                    style={{
                      width: 300,
                      maxWidth: '100%',
                      height: 120,
                      border: '1.5px dashed #CBD5E1',
                      borderRadius: 8,
                      cursor: 'crosshair',
                      touchAction: 'none',
                      display: 'block',
                    }}
                    data-testid="signature-canvas"
                  />
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={clearCanvas} type="button" data-testid="btn-clear">
                    <Eraser className="w-4 h-4 mr-1" /> Effacer
                  </Button>
                  {hasDrawn && <span className="text-xs text-green-600">✓ Signature tracée</span>}
                </div>
              </div>

              <Button
                onClick={submit}
                disabled={!canSubmit || submitting}
                className="w-full md:w-auto bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold"
                data-testid="btn-submit-sign"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                Valider ma signature électronique
              </Button>
            </div>
          )}
        </div>

        <div className="mt-4 text-center text-xs text-slate-500">
          Document sécurisé — Hash SHA-256 : <span className="font-mono">{doc.hash_sha256?.slice(0, 24)}…</span>
        </div>
      </div>
    </div>
  );
}
